import React from 'react';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { addMinutes, isAfter } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import useModal from '@/hooks/useModal';
import { useNotification } from '@/hooks';
import { Button, Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';
import AppRoutes from '@/const/routes';
import { UserI } from '@/interfaces';
import {
	caseApplicationsService,
	casesService,
	GET_CASE_APPLICATIONS,
	GET_CASE_ID,
} from '@/api';
import { GuidedTourModal } from '@/components/modals/guided-tour/GuidedTourModal';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';

interface Props {
	user: UserI;
	refetch: () => void;
}

const MINUTES = 10;

export const WelcomeModal: React.FC<Props> = ({ user, refetch }) => {
	const router = useRouter();
	const { case_id, evaluated } = router.query;
	const { Modal: ModalWelcome, show, hide } = useModal();
	const {
		Modal: ModalGuidedTour,
		show: showGuidedTour,
		hide: hideGuidedTour,
	} = useModal();
	const { toastError } = useNotification();

	const isQualified = (evaluated as string) === 'true';

	const { data: response } = useQuery({
		queryKey: [GET_CASE_APPLICATIONS, case_id],
		queryFn: () => caseApplicationsService.getCaseById(case_id as string),
	});

	const { data: caseDetailResponse } = useQuery({
		queryKey: [GET_CASE_ID, case_id],
		queryFn: () => casesService.getById(case_id as string),
		enabled: !!case_id && isQualified,
	});

	const defendantName = caseDetailResponse?.data?.defendant_incorporation?.name;

	const guidedTourSeenKey = `guided_tour_seen_${user.id}`;
	const [hasSeenTour] = React.useState(
		() => !!getLocalStorage(guidedTourSeenKey)
	);

	React.useEffect(() => {
		if (!response?.data?.application_date) return;

		const utcDate = new Date(response.data.application_date);
		const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const localDate = toZonedTime(utcDate, userTimezone);

		const unlockDate = addMinutes(localDate, MINUTES);
		const now = new Date();

		if (isAfter(unlockDate, now)) {
			setLocalStorage(guidedTourSeenKey, true);
			show();
			return;
		}

		toastError(
			'You’ve already applied to this case. You can view your submitted application in your My Cases page.'
		);
		cleanUrl();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response]);

	const cleanUrl = () => {
		router.replace(
			{
				pathname: router.pathname,
				query: {},
			},
			undefined,
			{ shallow: true }
		);
	};

	const handleClose = () => {
		hide();
		cleanUrl();
		refetch();
	};

	const handleGoToDashboard = () => {
		hide();
		hideGuidedTour();
		cleanUrl();
		refetch();

		if (!isQualified) {
			router.push(
				{
					pathname: AppRoutes.CLIENTAPP_ALL_CASES,
				},
				undefined,
				{ shallow: true }
			);
		}
	};

	const handleTakeTheTour = () => {
		setLocalStorage(guidedTourSeenKey, true);
		hide();
		showGuidedTour();
	};

	const handleGoToCase = () => {
		hide();
		hideGuidedTour();
		cleanUrl();
		refetch();

		if (case_id) {
			router.push(`${AppRoutes.CLIENTAPP_MY_CASES}/${case_id}`);
		}
	};

	return (
		<>
			<ModalWelcome
				avoidClose
				classNameCardContainer="!w-full md:!w-[730px] md:!max-w-[730px] !p-0 !rounded-[24px] !shadow-2xl bg-white"
				classNameContainerChildren="!max-h-none"
				noMaxHeight
			>
				<div className="relative flex flex-col items-center text-center px-8 py-10">
					<button
						type="button"
						onClick={handleClose}
						className="absolute right-6 top-6 outline-none"
					>
						<X className="w-6 h-6 text-black" />
					</button>

					{!hasSeenTour && (
						<div className="flex items-center justify-center gap-1 mb-8">
							<Typography type="bold-28" className="!text-black">
								Welcome to
							</Typography>
							<Typography type="bold-28" className="!text-primary">
								Claim Magic
							</Typography>
						</div>
					)}

					<div className="w-[138px] h-[138px] mx-auto mb-8">
						<Icon src={Icons.confirmComplete} className="w-full h-full" />
					</div>

					<Typography type="bold-36" className="!text-2xl xl:!text-[32px]">
						{isQualified
							? 'You qualified!'
							: "Sorry, you're not eligible for this case"}
					</Typography>

					<Typography type="regular-18" className="mx-auto mt-8">
						{isQualified
							? `You're a step closer to holding ${
									defendantName ?? 'them'
							  } accountable.`
							: 'Explore other open cases—you may be eligible!'}
					</Typography>

					<div
						className={clsx(
							'flex flex-col md:flex-row items-center gap-3 mt-10 w-full',
							{
								'justify-between': isQualified || !hasSeenTour,
								'justify-center': !isQualified && hasSeenTour,
							}
						)}
					>
						{!isQualified && hasSeenTour ? (
							<Button
								decoration="primary"
								size="fit"
								onClick={handleGoToDashboard}
							>
								Explore All Cases
							</Button>
						) : (
							<>
								<Button
									decoration="line-primary"
									size="fit"
									onClick={handleGoToDashboard}
								>
									Go to your dashboard
								</Button>
								<Button
									decoration="primary"
									size="fit"
									onClick={
										isQualified && hasSeenTour
											? handleGoToCase
											: handleTakeTheTour
									}
								>
									{isQualified && hasSeenTour ? 'Go to Case' : 'Take the tour'}
								</Button>
							</>
						)}
					</div>
				</div>
			</ModalWelcome>

			<GuidedTourModal
				Modal={ModalGuidedTour}
				onClose={hideGuidedTour}
				onFinish={handleGoToDashboard}
			/>
		</>
	);
};
